import { useMemo } from "react";

import { typedQueryClient } from "hooks";
import { CustomersQueryKeys } from "modules/CustomersDashboard/queryKeys";

import type { WebSocketMessageListener } from "components";
import type { Customer, CustomerNote } from "models";
import type { ListCustomersResponse } from "modules/CustomersDashboard/queryKeys";

const getUpdatedCustomersUpsertNote = (note: CustomerNote, customers: ListCustomersResponse) => {
  const customerIdx = customers.items.findIndex(c => c.id === note.customer_id);
  if (customerIdx < 0) return;

  const customer = customers.items[customerIdx];
  const noteIdx = customer.notes?.findIndex(n => n.id === note.id) ?? -1;

  const updatedCustomerNotes = [...(customer.notes ?? [])];

  if (noteIdx < 0) updatedCustomerNotes.push(note);
  else updatedCustomerNotes[noteIdx] = note;

  return customers.items.with(customerIdx, { ...customer, notes: updatedCustomerNotes });
};

const getUpdatedCustomersRemovedNote = (note: CustomerNote, customers: ListCustomersResponse) => {
  const customerIdx = customers.items.findIndex(c => c.id === note.customer_id);
  if (customerIdx < 0) return;

  const customer = customers.items[customerIdx];
  if (!customer.notes) return;

  const noteIdx = customer.notes.findIndex(n => n.id === note.id);
  if (noteIdx < 0) return;

  return customers.items.with(customerIdx, { ...customer, notes: customer.notes.toSpliced(noteIdx, 1) });
};

const getUpdatedSelectedCustomerNoteUpsert = (note: CustomerNote, selectedCustomer: Customer) => {
  const noteIdx = selectedCustomer.notes?.findIndex(n => n.id === note.id) ?? -1;
  const updatedCustomerNotes = [...(selectedCustomer.notes ?? [])];

  if (noteIdx < 0) updatedCustomerNotes.push(note);
  else updatedCustomerNotes[noteIdx] = note;

  return { ...selectedCustomer, notes: updatedCustomerNotes };
};

export const useCustomersDashboardListeners = () => {
  const listeners = useMemo(() => {
    const customerNoteListeners: WebSocketMessageListener<CustomerNote>[] = [
      {
        model: "CustomerNote",
        action: "upsert",
        callback: ({ data: note }) => {
          const listCustomers = typedQueryClient.getQueryData(CustomersQueryKeys.list);
          const selectedCustomer = typedQueryClient.getQueryData(CustomersQueryKeys.selectedCustomer);
          if (!listCustomers && !selectedCustomer) return;

          if (listCustomers) {
            const updatedListCustomers = getUpdatedCustomersUpsertNote(note, listCustomers);
            if (updatedListCustomers) typedQueryClient.setQueryData(CustomersQueryKeys.list, { ...listCustomers, items: updatedListCustomers });
          }

          if (selectedCustomer && note.customer_id === selectedCustomer.id) {
            typedQueryClient.setQueryData(CustomersQueryKeys.selectedCustomer, getUpdatedSelectedCustomerNoteUpsert(note, selectedCustomer));
          }
        }
      },
      {
        model: "CustomerNote",
        action: "delete",
        callback: ({ data: note }) => {
          const listCustomers = typedQueryClient.getQueryData(CustomersQueryKeys.list);
          const selectedCustomer = typedQueryClient.getQueryData(CustomersQueryKeys.selectedCustomer);
          if (!listCustomers && !selectedCustomer) return;

          if (listCustomers) {
            const updatedListCustomers = getUpdatedCustomersRemovedNote(note, listCustomers);
            if (updatedListCustomers) typedQueryClient.setQueryData(CustomersQueryKeys.list, { ...listCustomers, items: updatedListCustomers });
          }

          if (selectedCustomer) {
            if (note.customer_id === selectedCustomer.id && selectedCustomer.notes) {
              const noteIdx = selectedCustomer.notes.findIndex(n => n.id === note.id);
              if (noteIdx < 0) return;

              typedQueryClient.setQueryData(CustomersQueryKeys.selectedCustomer, { ...selectedCustomer, notes: selectedCustomer.notes.toSpliced(noteIdx, 1) });
            }
          }
        }
      }
    ];

    return [...customerNoteListeners];
  }, []);

  return listeners;
};
